export enum BucketTypeHttpValue {
  GoogleCloudStorage = 'google_cloud_storage',
  AzureBlobStorage = 'azure_blob_storage',
  AmazonS3Storage = 'amazon_s3',
}

export enum DatabaseTypeHttpValue {
  BigQuery = 'bigquery',
  Snowflake = 'snowflake',
}

export interface OrgSpaceHttpModel {
  company_id: string;
  data_warehouse: DataWarehouseHttpModel;
  gitlab_group_id: string;
  gitlab_user_id: string;
  id: string;
  is_dbt: boolean;
  is_default: boolean;
  job_retention_time_minutes: number;
  orchestration_branches: string[];
  public_schema_name: string;
  repo_full_name: string;
  repo_name: string;
  secret_id: string;
  name?: string;
  space_name?: string;
  view_branches: string[];
}

export interface DataWarehouseBucketHttpModel<T extends BucketTypeHttpValue = BucketTypeHttpValue> {
  type: T;
}

export interface DataWarehouseGoogleCloudStorageBucketHttpModel
  extends DataWarehouseBucketHttpModel<BucketTypeHttpValue.GoogleCloudStorage> {
  name: string;
  project_id: string;
}

export interface DataWarehouseAzureBlobStorageBucketHttpModel
  extends DataWarehouseBucketHttpModel<BucketTypeHttpValue.AzureBlobStorage> {
  account_name: string;
  container_name: string;
  subscription_id: string;
  resource_group: string;
}

export interface DataWarehouseAmazonS3StorageBucketHttpModel
  extends DataWarehouseBucketHttpModel<BucketTypeHttpValue.AmazonS3Storage> {
  name: string;
  region: string;
}

export interface DataWarehouseDatabaseHttpModel<T extends DatabaseTypeHttpValue = DatabaseTypeHttpValue> {
  type: T;
}

export interface DataWarehouseBigQueryDatabaseHttpModel
  extends DataWarehouseDatabaseHttpModel<DatabaseTypeHttpValue.BigQuery> {
  name: string;
  project_id: string;
}

export interface DataWarehouseSnowflakeDatabaseHttpModel
  extends DataWarehouseDatabaseHttpModel<DatabaseTypeHttpValue.Snowflake> {
  account: string;
  name: string;
  user: string;
  warehouse: string;
}

export interface DataWarehouseHttpModel {
  bucket: DataWarehouseBucketHttpModel;
  database: DataWarehouseDatabaseHttpModel;
}

export interface ErrorResponseHttpModel {
  error_message: string;
}

export interface SpacesResponseHttpModel {
  spaces: OrgSpaceHttpModel[];
}

export interface SpaceCreateRequestHttpModel {
  space_name: string;
  database: DataWarehouseRequestHttpModel;
  is_dbt_space: boolean;
}

export interface ServiceAccountHttpModel {
  auth_provider_x509_cert_url: string;
  auth_uri: string;
  client_email: string;
  client_id: string;
  client_x509_cert_url: string;
  private_key: string;
  private_key_id: string;
  project_id: string;
  token_uri: string;
  type: 'service_account';
}

export interface GoogleCloudStorageDataWarehouseRequestHttpModel {
  secret: ServiceAccountHttpModel;
  location: string;
}

export type BigQueryGoogleCloudStorageDataWarehouseRequestHttpModel = GoogleCloudStorageDataWarehouseRequestHttpModel;

export interface SnowflakeDataWarehouseRequestHttpModel {
  user: string;
  password: string;
  account: string;
  warehouse: string;
  database: string;
}

export interface AzureBlobStorageDataWarehouseRequestHttpModel {
  azure_creds: {
    account_key: string;
    account_name: string;
    container_name: string;
  };
}

export interface AmazonS3StorageDataWarehouseRequestHttpModel {
  amazon_s3: {
    access_key_id: string;
    region: string;
    secret_access_key: string;
  };
}

export type SnowflakeAzureBlobStorageDataWarehouseRequestHttpModel = SnowflakeDataWarehouseRequestHttpModel &
  AzureBlobStorageDataWarehouseRequestHttpModel;

export type SnowflakeAmazonS3StorageDataWarehouseRequestHttpModel = SnowflakeDataWarehouseRequestHttpModel &
  AmazonS3StorageDataWarehouseRequestHttpModel;

export type DataWarehouseRequestHttpModel =
  | BigQueryGoogleCloudStorageDataWarehouseRequestHttpModel
  | SnowflakeAzureBlobStorageDataWarehouseRequestHttpModel
  | SnowflakeAmazonS3StorageDataWarehouseRequestHttpModel;

export interface DataWarehouseConfig {
  googleCloudStorageConfig?: GoogleCloudStorageConfiguration;
  snowflakeConfig?: SnowflakeConfiguration;
  amazonS3StorageConfig?: AmazonS3StorageConfiguration;
  azureBlobStorageConfig?: AzureBlobStorageConfiguration;
}

export interface GoogleCloudStorageConfiguration {
  serviceAccount: ServiceAccountHttpModel;
  location: string;
}

export interface AzureBlobStorageConfiguration {
  accountKey: string;
  accountName: string;
  containerName: string;
}

export interface SnowflakeConfiguration {
  account: string;
  database: string;
  password: string;
  user: string;
  warehouse: string;
}

export interface AmazonS3StorageConfiguration {
  accessKeyId: string;
  region: string;
  secretAccessKey: string;
}
